import { useAssetModalState } from "../hooks/useAssetModalState";
import { useImageAssets } from "../hooks/useImageAssets";
import { useImageUploadCommit } from "../hooks/useImageUploadCommit";
import { EMPTY_MESSAGE, TAB_LABEL } from "../lib/asset-modal-tabs";
import type { ComponentReplacementSelection } from "../lib/component-replacement";
import { AssetBrowserTab } from "./AssetBrowserTab";
import { AssetModalChrome, TitleWithSubject } from "./AssetModalChrome";
import { AssetPreviewLightbox } from "./AssetPreviewLightbox";
import { ImageAssetUploadFields } from "./ImageAssetUploadFields";
import { SaveAsAssetCheckbox } from "./SaveAsAssetCheckbox";

type ReplaceTab = "assets" | "image";

const TABS = [
  { id: "assets" as ReplaceTab, label: TAB_LABEL.assets },
  { id: "image" as ReplaceTab, label: TAB_LABEL.image },
];

interface ReplaceComponentModalProps {
  isOpen: boolean;
  /** Component being replaced, e.g. "Gaming Chair". Omitted when opened from
   *  a selection, where the component is identified after the asset is picked. */
  groupName?: string;
  /** Project of the refined image; a saved asset is attached to it. */
  projectId?: string | null;
  onSelect: (selection: ComponentReplacementSelection) => void;
  onClose: () => void;
}

export function ReplaceComponentModal({
  isOpen,
  groupName,
  projectId,
  onSelect,
  onClose,
}: ReplaceComponentModalProps) {
  const state = useAssetModalState<ReplaceTab>(isOpen, "assets");
  const isAssetsTab = state.activeTab === "assets";
  const imageAssets = useImageAssets(isOpen && isAssetsTab);

  const upload = useImageUploadCommit<ComponentReplacementSelection>({
    isOpen,
    isImageTab: !isAssetsTab,
    file: state.file,
    name: state.name,
    saveAsAsset: state.saveAsAsset,
    projectId,
    fromUpload: (url, name) => ({ url, name, assetId: null }),
    fromAsset: (asset) => ({
      url: asset.value,
      name: asset.name,
      assetId: asset.id,
    }),
    onCommit: (selection) => {
      onSelect(selection);
      onClose();
    },
  });

  if (!isOpen) return null;

  const selectedAsset =
    imageAssets.assets.find((a) => a.id === state.selectedAssetId) ?? null;
  const okDisabled =
    upload.isPending ||
    (isAssetsTab
      ? selectedAsset === null
      : !state.file || state.name.trim().length === 0);

  const handleOk = () => {
    if (isAssetsTab) {
      if (!selectedAsset || upload.isPending) return;
      onSelect({
        url: selectedAsset.value,
        name: selectedAsset.name,
        assetId: selectedAsset.id,
      });
      onClose();
      return;
    }
    upload.mutate();
  };

  return (
    <>
      <AssetModalChrome
        ariaContext="replace component"
        title={
          groupName ? (
            <TitleWithSubject prefix="Replace" subject={groupName} />
          ) : (
            "Replace"
          )
        }
        // The component is already named in the title; repeating it here would
        // read as a stutter — and make the name ambiguous to query.
        subtitle={
          groupName
            ? "Select an image to replace this element."
            : "Select an image to use as the replacement."
        }
        tabs={TABS}
        activeTab={state.activeTab}
        onTabChange={state.setActiveTab}
        footerLeft={
          isAssetsTab ? undefined : (
            <SaveAsAssetCheckbox
              checked={state.saveAsAsset}
              onChange={state.setSaveAsAsset}
              label="Save image as asset"
            />
          )
        }
        primary={{
          label: upload.isPending ? "Replacing…" : "Replace",
          disabled: okDisabled,
          onClick: handleOk,
        }}
        onClose={onClose}
      >
        {isAssetsTab ? (
          <AssetBrowserTab
            query={imageAssets}
            projectId={projectId}
            enabled={isOpen && isAssetsTab}
            selectedAssetId={state.selectedAssetId}
            onSelect={(asset) => state.setSelectedAssetId(asset.id)}
            onPreview={state.setPreviewAsset}
            emptyMessage={EMPTY_MESSAGE.imageAsset}
          />
        ) : (
          <>
            <ImageAssetUploadFields
              file={state.file}
              previewUrl={state.previewUrl}
              onSelectFile={state.setFile}
              name={state.name}
              onNameChange={state.setName}
              onSubmit={() => {
                if (!okDisabled) handleOk();
              }}
            />

            {upload.isError && (
              <p className="mt-4 text-sm text-red-400" role="alert">
                Couldn&apos;t use this image. Please try again.
              </p>
            )}
          </>
        )}
      </AssetModalChrome>

      <AssetPreviewLightbox
        asset={state.previewAsset}
        onClose={() => state.setPreviewAsset(null)}
        onUse={(asset) => {
          state.setSelectedAssetId(asset.id);
          state.setPreviewAsset(null);
        }}
      />
    </>
  );
}
